/**
 * Services Initializer Component
 * Injects persisted last GPS slice into GPS services after persistence boot.
 */

import React, { useEffect, useRef } from "react";
import { rawCompassDataService } from "../services/navigation";
import { filteredGpsDataService } from "../services/navigation/FilteredGpsDataService";
import { gpsPredictionService } from "../services/navigation/GpsPredictionService";
import { rawGpsDataService } from "../services/navigation/RawGpsDataService";
import { ephemeralStore, servicesBootstrapSlice } from "../store/ephemeral";
import { lastGpsDataSlice, usePersistedSlice, usePersistenceReady } from "../store/persistence";
import { logAppMemoryReport } from "../tools/AppMemoryProfiler.tools";

/**
 * Component that initializes GPS services after persistence hydration
 * This component should be rendered inside MainProvider
 */
export const ServicesInitializer: React.FC<{ "children": React.ReactNode }> = ( { children } ) => {
    const [ lastGpsData, setLastGpsData ] = usePersistedSlice( lastGpsDataSlice );
    const persistenceReady = usePersistenceReady();
    const initialized = useRef( false );
    useEffect( () => {
        if ( !persistenceReady ) {
            return;
        }

        /*
         * Full boot once. Also recover after Vite HMR: the navigation service modules may be
         * replaced with a fresh inactive singleton while this ref stays true.
         */
        const needsFullBoot = !initialized.current;
        const needsCompassRecover = initialized.current && !rawCompassDataService.isActive();

        if ( !needsFullBoot && !needsCompassRecover ) {
            return;
        }

        if ( needsFullBoot ) {
            console.log( "🔧 [ServicesInitializer] Initializing GPS services..." );
            rawGpsDataService.initLastValue( lastGpsData );
        } else {
            console.log( "🔧 [ServicesInitializer] Recovering compass after HMR / inactive singleton..." );
        }

        const initializeServices = async() => {
            if ( needsFullBoot ) {
                console.log( "🔧 [ServicesInitializer] Injecting persisted GPS slice into GPS services..." );

                await rawGpsDataService.start( setLastGpsData );

                filteredGpsDataService.start( lastGpsData );

                gpsPredictionService.start();
            }

            await rawCompassDataService.start();

            initialized.current = true;

            ephemeralStore.setValue( servicesBootstrapSlice, { "servicesReady": true } );

            console.log( "✅ [ServicesInitializer] Persisted GPS slice injected into all GPS services" );

            if ( needsFullBoot ) {
                logAppMemoryReport( {
                    "context":      "services-initialized",
                    "skipThrottle": true,
                } );
            }
        };

        void initializeServices();
    }, [ lastGpsData, persistenceReady, setLastGpsData ] );


    return ( <>
{children}
             </> );
};
